'use client';

import { type CSSProperties, forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { AvatarRootContext } from './context/avatar-root.context';
import { useGetInitials } from './hooks/use-get-initials';
import { avatarFullNameRecipe } from './styles/avatar-full-name.styles';
import type { AvatarFullNamePropsInterface } from './types/avatar-full-name.types';

/**
 * @since 10.0.0
 */
export const AvatarFullName = forwardRef<HTMLDivElement, AvatarFullNamePropsInterface>(
  (props, forwardedRef) => {
    const { children, className, css: cssProp, ...rest } = props;

    const fullInitials = useGetInitials(children);
    const singleInitial = useGetInitials(children, true);

    const { size = 'lg' } = useContext(AvatarRootContext);

    const styles = css(avatarFullNameRecipe.raw({ size }), cssProp);

    return (
      <div
        data-c11n-component="Avatar.FullName"
        data-c11n-version={__C11N_VERSION__}
        ref={forwardedRef}
        className={cx('c11n', styles, className)}
        style={
          {
            '--c11n-initials-full': `'${fullInitials}'`,
            '--c11n-initials-single': `'${singleInitial}'`,
          } as CSSProperties
        }
        {...rest}
      >
        <span className={css({ srOnly: true })}>{children}</span>
      </div>
    );
  },
);

AvatarFullName.displayName = 'Avatar.FullName';
